import { defineStore } from "pinia";
import axios from "axios";

export const useWebProfileStore = defineStore("WebProfile", {
    state: () => ({
        profile: {
            id: null,
            photo: null,
            name: "",
            availability: "",
            region: "",
            country: null,
            country_name: null,
            custom_region: null,
            claim: null,
            url: null,
            meta_title: null,
            meta_description: null,
            summary: null,
            lang: null,
            group: null,
        },

        webProfile: {
            area_of_expertise: [],
            verticals: [],
        },

        webProfileId: null,
        lastPublished: null,
        lastSaved: null,
        lastSubmitted: null,
        hasConsentedToPublish: false,
        status: null,
        topCompetences: [],
        detailedCompetences: [],
        industrySectors: [],
        jobTags: [],
        projects: [],
        education: [],
        publications: [],
        claimProposals: [],
        useDefaultPhoto: false,
        path: null,
        drupalUrl: null,
        aiContentStatus: null,

        availableAoE: [],
        availableVerticals: [],

        isEditSummaryModalOpen: false,
        isEditClaimModalOpen: false,
        isEditCompetencesModalOpen: false,
        isEditDetailedCompetencesModalOpen: false,
        isEditIndustrySectorsModalOpen: false,
        isEditJobTagsModalOpen: false,
        isEditVerticalsModalOpen: false,
        isEditAoEModalOpen: false,
        isEditUrlModalOpen: false,
        isEditMetaModalOpen: false,
        isEditMetaTitleModalOpen: false,
        isEditClaimProposalsModalOpen: false,
        isEditRegionModalOpen: false,
        isVersionHistoryModalOpen: false,
        
        historyField: null,
        historyData: [],
        
        isAdmin: false,
        profileExperiencesForSectors: [],
        allIndustrySectors: [],
    }),
    actions: {
        initAxiosCsrf() {
            const token = document.head.querySelector(
                'meta[name="csrf-token"]'
            );
            if (token) {
                axios.defaults.headers.common["X-CSRF-TOKEN"] = token.content;
                axios.defaults.headers.common["X-Requested-With"] =
                    "XMLHttpRequest";
                axios.defaults.withCredentials = true;
            }
        },
        setWebProfileData(data) {
            if (!data) return;
            this.webProfileId = data.id;
            this.profile.id = data.profile_id;
            this.profile.photo = data.photo;
            this.profile.name = data.profile_name;
            this.profile.availability = data.availability;
            this.profile.region = data.region;
            this.profile.country = data.country;
            this.profile.country_name = data.country_name;
            this.profile.custom_region = data.custom_region;
            this.profile.claim = data.claim;
            this.profile.summary = data.summary;
            this.profile.group = data.group_short;
            this.profile.lang = data.lang;
            this.profile.url = data.url;
            this.profile.meta_description = data.meta_description;
            this.profile.meta_title = data.meta_title;

            this.lastPublished = data.publication_date;
            this.lastSaved = data.updated_at;
            this.lastSubmitted = data.last_submitted_date;
            this.hasConsentedToPublish = !!data.has_consented_to_publish;

            this.status = data.status;
            this.path = data.path;
            this.drupalUrl = data.drupal_url;
            this.isAdmin = data.is_admin;
            this.claimProposals = data.claim_proposals || [];

            this.topCompetences = data.skills_main || [];
            this.detailedCompetences = data.skills_additional || [];
            this.industrySectors = data.industry_sectors || [];
            this.jobTags = data.job_tags || [];

            this.webProfile.area_of_expertise = data.area_of_expertise || [];
            this.webProfile.verticals = data.verticals || [];

            this.webProfile.area_of_expertise_name = data.area_of_expertise_name;
            this.webProfile.verticals_name = data.verticals_name;

            const expData = data.experience || [];
            this.projects = Array.isArray(expData)
                ? expData
                : Object.values(expData);

            const eduData = data.education || [];
            this.education = Array.isArray(eduData)
                ? eduData
                : Object.values(eduData);

            this.publications = data.publications || [];
            this.useDefaultPhoto = data.use_default_photo;
            this.aiContentStatus = data.ai_content_status;
        },
        updateGeneratedContent(data) {
            this.aiContentStatus = data.ai_content_status;
            if (data.claim) this.profile.claim = data.claim;
            if (data.summary) this.profile.summary = data.summary;
            if (data.meta_title) this.profile.meta_title = data.meta_title;
            if (data.meta_description) this.profile.meta_description = data.meta_description;
            if (data.url) this.profile.url = data.url;
        },
        openEditRegionModal() {
            this.isEditRegionModalOpen = true;
        },
        closeEditRegionModal() {
            this.isEditRegionModalOpen = false;
        },
        openEditClaimModal() {
            this.isEditClaimModalOpen = true;
        },
        closeEditClaimModal() {
            this.isEditClaimModalOpen = false;
        },
        openEditCompetencesModal() {
            this.isEditCompetencesModalOpen = true;
        },
        closeEditCompetencesModal() {
            this.isEditCompetencesModalOpen = false;
        },
        openEditDetailedCompetencesModal() {
            this.isEditDetailedCompetencesModalOpen = true;
        },
        closeEditDetailedCompetencesModal() {
            this.isEditDetailedCompetencesModalOpen = false;
        },
        openEditIndustrySectorsModal() {
            this.isEditIndustrySectorsModalOpen = true;
        },
        closeEditIndustrySectorsModal() {
            this.isEditIndustrySectorsModalOpen = false;
            this.profileExperiencesForSectors = [];
            this.allIndustrySectors = [];
        },
        openEditJobTagsModal() {
            this.isEditJobTagsModalOpen = true;
        },
        closeEditJobTagsModal() {
            this.isEditJobTagsModalOpen = false;
        },
        openEditSummaryModal() {
            this.isEditSummaryModalOpen = true;
        },
        closeEditSummaryModal() {
            this.isEditSummaryModalOpen = false;
        },
        openEditAoEModal() {
            this.isEditAoEModalOpen = true;
        },
        closeEditAoEModal() {
            this.isEditAoEModalOpen = false;
        },
        openEditVerticalsModal() {
            this.isEditVerticalsModalOpen = true;
        },
        closeEditVerticalsModal() {
            this.isEditVerticalsModalOpen = false;
        },
        openEditUrlModal() {
            this.isEditUrlModalOpen = true;
        },
        closeEditUrlModal() {
            this.isEditUrlModalOpen = false;
        },
        openEditMetaModal() {
            this.isEditMetaModalOpen = true;
        },
        closeEditMetaModal() {
            this.isEditMetaModalOpen = false;
        },
        openEditMetaTitleModal() {
            this.isEditMetaTitleModalOpen = true;
        },
        closeEditMetaTitleModal() {
            this.isEditMetaTitleModalOpen = false;
        },
        openEditClaimProposalsModal() {
            this.isEditClaimProposalsModalOpen = true;
        },
        closeEditClaimProposalsModal() {
            this.isEditClaimProposalsModalOpen = false;
        },
        async openVersionHistoryModal(field) {
            this.historyField = field;
            await this.fetchVersionHistory(field);
            this.isVersionHistoryModalOpen = true;
        },
        closeVersionHistoryModal() {
            this.isVersionHistoryModalOpen = false;
            this.historyData = [];
            this.historyField = null;
        },
        updateRegion(newRegion, showLoader = true) {
            this.saveWebProfileSection("custom_region", newRegion, showLoader);
            return this.closeEditRegionModal();
        },
        updateClaim(newClaim, showLoader = true) {
            this.saveWebProfileSection("claim", newClaim, showLoader);
            return this.closeEditClaimModal();
        },
        updateSummary(newSummary, showLoader = true) {
            this.saveWebProfileSection("summary", newSummary, showLoader);
            return this.closeEditSummaryModal();
        },
        updateUrl(newUrl, showLoader = true) {
            this.saveWebProfileSection("url", newUrl, showLoader);
            return this.closeEditUrlModal();
        },
        updateMetaTitle(newMetaTitle, showLoader = true) {
            this.saveWebProfileSection("meta_title", newMetaTitle, showLoader);
            return this.closeEditMetaTitleModal();
        },
        updateMeta(newMeta, showLoader = true) {
            this.saveWebProfileSection("meta_description", newMeta, showLoader);
            return this.closeEditMetaModal();
        },
        async fetchWebProfileData(profileId) {
            this.initAxiosCsrf();
            if (!profileId) {
                console.error("No Profile ID provided to fetchWebProfileData");
                return;
            }

            try {
                this.$functions.showLoader();
                const response = await axios.get(
                    `/candidates/${profileId}/web-profile/data`
                );
                this.setWebProfileData(response.data.data);
            } catch (error) {
                console.error("Error fetching WebProfile data:", error);
            } finally {
                this.$functions.hideLoader();
            }
        },
        async fetchVersionHistory(field) {
            this.initAxiosCsrf();
            if (!this.profile.id) return;

            try {
                this.$functions.showLoader();
                const response = await axios.get(
                    `/candidates/${this.profile.id}/web-profile/history/${field}`
                );
                this.historyData = response.data.data || [];
            } catch (error) {
                console.error("Error fetching history:", error);
            } finally {
                this.$functions.hideLoader();
            }
        },
        async saveWebProfileSection(section, data, showLoader = true) {
            this.initAxiosCsrf();
            if (!this.profile.id) {
                console.error("Cannot save, profile ID is missing.");
                return;
            }

            try {
                if (showLoader) {
                    this.$functions.showLoader();
                }
                const response = await axios.put(
                    `/candidates/${this.profile.id}/web-profile/update`,
                    { section, data }
                );
                this.setWebProfileData(response.data.data);
            } catch (error) {
                console.error(`Error saving section '${section}':`, error);
            } finally {
                this.$functions.hideLoader();
            }
        },
        async saveDomainSection(profileId, section) {
            const data = section === 'aoe' 
                ? this.webProfile.area_of_expertise 
                : this.webProfile.verticals;

            try {
                await this.saveWebProfileSection(section, data);
                if (section === 'aoe') this.closeEditAoEModal();
                if (section === 'verticals') this.closeEditVerticalsModal();
            } catch (error) {
                console.error(error);
            }
        },
        async updateTopCompetences(selectedItems) {
            if (!selectedItems || selectedItems.length === 0) {
                return this.saveWebProfileSection("skills_main", []);
            }
            const maxDurationInMonths = Math.max(
                ...selectedItems.map((i) => Number(i.duration_in_month))
            );
            const sortedItems = [...selectedItems]
                .sort((a, b) => b.duration_in_month - a.duration_in_month)
                .map((i) => ({
                    ...i,
                    duration_percentage:
                        maxDurationInMonths > 0
                            ? Math.round(
                                  (i.duration_in_month / maxDurationInMonths) *
                                      100
                              )
                            : 0,
                }));

            await this.saveWebProfileSection("skills_main", sortedItems);
            return this.closeEditCompetencesModal();
        },
        async updateDetailedCompetences(selectedItems) {
            if (!selectedItems || selectedItems.length === 0) {
                return this.saveWebProfileSection("skills_additional", []);
            }
            const maxDurationInMonths = Math.max(
                ...selectedItems.map((i) => Number(i.duration_in_month))
            );
            const sortedItems = [...selectedItems]
                .sort((a, b) => b.duration_in_month - a.duration_in_month)
                .map((i) => ({
                    ...i,
                    duration_percentage:
                        maxDurationInMonths > 0
                            ? Math.round(
                                  (i.duration_in_month / maxDurationInMonths) *
                                      100
                              )
                            : 0,
                }));

            await this.saveWebProfileSection("skills_additional", sortedItems);
            return this.closeEditDetailedCompetencesModal();
        },
        updateClaimFromProposals(newClaim, showLoader = true) {
            this.saveWebProfileSection("claim_from_proposals", newClaim, showLoader);
            return this.closeEditClaimProposalsModal();
        },
        saveIndustrySectorAssignments(assignments) {
            if (!this.profile.id) {
                console.error("Cannot save, profile ID is missing.");
                throw new Error("Profile ID is missing.");
            }

            try {
                return this.saveWebProfileSection(
                    "industry_sectors",
                    assignments
                );
            } catch (error) {
                console.error(
                    "Error saving industry sector assignments:",
                    error
                );
                throw error;
            }
        },
        saveJobTagsAssignments(assignments) {
            if (!this.profile.id) {
                console.error("Cannot save, profile ID is missing.");
                throw new Error("Profile ID is missing.");
            }

            try {
                this.$functions.showLoader();

                return this.saveWebProfileSection("job_tags", assignments);
            } catch (error) {
                console.error("Error saving job tags assignments:", error);
                throw error;
            }
        },
        async submitWebProfile() {
            this.initAxiosCsrf();
            if (!this.profile.id) return;

            try {
                this.$functions.showLoader();
                const response = await axios.get(
                    `/candidates/${this.profile.id}/web-profile/submit`
                );
                
                if (response.data.status === 'success') {
                    this.status = response.data.data.status;
                    await this.fetchWebProfileData(this.profile.id);
                    return true;
                }
                return false;
            } catch (error) {
                console.error("Error submitting WebProfile:", error);
                return false;
            } finally {
                this.$functions.hideLoader();
            }
        },
        async previewWebProfile(profileId) {
            try {
                this.$functions.showLoader();

                const payloadResponse = await axios.get(
                    `/admin/web-profiles/${profileId}/payload`
                );
                const payload = payloadResponse.data.data;
                const token = payloadResponse.data.hash
                if (!payload) {
                    console.error("No payload received from server");
                    return null;
                }

                const form = document.createElement("form");
                form.method = "POST";
                form.action = this.drupalUrl + "/de/member-profile-preview";
                form.target = "_blank";

                const input = document.createElement("input");
                input.type = "hidden";
                input.name = 'data';
                input.value = payload;
                form.appendChild(input);

                const input2 = document.createElement("input");
                input2.type = "hidden";
                input2.name = 'token';
                input2.value = token;
                form.appendChild(input2);

                document.body.appendChild(form);
                form.submit();
                document.body.removeChild(form);
            } catch (error) {
                console.error("Error sending payload directly:", error);
                throw error;
            } finally {
                this.$functions.hideLoader();
            }
        },
    },
});